import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * The top-bar location switcher (§15z: "All locations (n)" — scope OPEN, V16).
 * Phase 1 only records the choice; Products / Promotions / Orders read it in
 * their phases. `'all'` is the default and means every location.
 */
export type LocationScope = 'all' | string;

interface LocationScopeValue {
  scope: LocationScope;
  setScope: (scope: LocationScope) => void;
}

const Ctx = createContext<LocationScopeValue | null>(null);

export function LocationScopeProvider({ children, vendorId }: { children: ReactNode; vendorId: string | null }) {
  const storageKey = vendorId ? `glp.vendor.locationScope.${vendorId}` : null;
  const [scope, setScopeState] = useState<LocationScope>('all');
  useEffect(() => {
    if (!storageKey) { setScopeState('all'); return; }
    try { setScopeState(window.localStorage.getItem(storageKey) || 'all'); } catch { setScopeState('all'); }
  }, [storageKey]);
  const setScope = (next: LocationScope) => {
    setScopeState(next);
    if (storageKey) { try { window.localStorage.setItem(storageKey, next); } catch { /* storage is optional */ } }
  };
  const value = useMemo(() => ({ scope, setScope }), [scope, storageKey]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLocationScope(): LocationScopeValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useLocationScope must be used within a LocationScopeProvider');
  return v;
}
